/** Motion Reconstruction API helpers — video input + multi-person track selection. */

export type MotionReconstructionStatus =
  | 'WAITING'
  | 'READY'
  | 'SUBMITTING'
  | 'QUEUED'
  | 'RUNNING'
  | 'SELECTION_REQUIRED'
  | 'COMPLETED'
  | 'FAILED'

export interface MotionTrackSummary {
  track_id: string
  frames_present: number
  coverage: number
  mean_confidence: number
  representative_frame_index?: number
  representative_timestamp_ms?: number
}

export const LOW_QUALITY_COVERAGE_THRESHOLD = 0.25
export const LOW_QUALITY_CONFIDENCE_THRESHOLD = 0.35

export function mapReconstructionApiStatus(status: string | undefined): MotionReconstructionStatus {
  switch (status) {
    case 'QUEUED':
      return 'QUEUED'
    case 'RUNNING':
      return 'RUNNING'
    case 'SELECTION_REQUIRED':
      return 'SELECTION_REQUIRED'
    case 'COMPLETED':
      return 'COMPLETED'
    case 'FAILED':
      return 'FAILED'
    default:
      return 'READY'
  }
}

export type MotionQualityMode = 'fast' | 'balanced'

export function normalizeMotionQualityMode(raw: unknown): MotionQualityMode {
  return raw === 'fast' ? 'fast' : 'balanced'
}

/** Fingerprint of inputs that invalidate a COMPLETED result (needsRegeneration). */
export function motionReconstructionResultFingerprint(args: {
  inputVideoAssetId?: string | null
  selectedTrackId?: string | null
  qualityMode?: string | null
  prompt?: string | null
}): string {
  return [
    args.inputVideoAssetId || '',
    args.selectedTrackId || '',
    normalizeMotionQualityMode(args.qualityMode),
    (args.prompt || '').trim(),
  ].join('|')
}

export function buildMotionReconstructionPayload(
  inputVideoAssetId: string,
  qualityMode = 'balanced',
  selectedTrackId?: string | null,
) {
  return {
    input_video_asset_id: inputVideoAssetId,
    quality_mode: normalizeMotionQualityMode(qualityMode),
    ...(selectedTrackId ? { selected_track_id: selectedTrackId } : {}),
  }
}

export function trackDisplayIndex(trackId: string, orderedTracks: MotionTrackSummary[]): number {
  const idx = orderedTracks.findIndex((t) => t.track_id === trackId)
  return idx >= 0 ? idx + 1 : 1
}

export function isLowQualityTrack(track: MotionTrackSummary): boolean {
  return (
    track.coverage < LOW_QUALITY_COVERAGE_THRESHOLD ||
    track.mean_confidence < LOW_QUALITY_CONFIDENCE_THRESHOLD
  )
}

export function formatCoveragePercent(coverage: number): string {
  return `${Math.round(Math.max(0, Math.min(1, coverage)) * 100)}%`
}

export function confidenceQualityLabel(meanConfidence: number): 'high' | 'medium' | 'low' {
  if (meanConfidence >= 0.55) return 'high'
  if (meanConfidence >= 0.35) return 'medium'
  return 'low'
}

export function shouldPollReconstructionStatus(status: MotionReconstructionStatus): boolean {
  return status === 'QUEUED' || status === 'RUNNING' || status === 'SELECTION_REQUIRED'
}

export function isReconstructionBusy(status: MotionReconstructionStatus): boolean {
  return status === 'SUBMITTING' || status === 'QUEUED' || status === 'RUNNING'
}
